import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PenTool, Loader2 } from 'lucide-react';
import TermsSignaturePad from './TermsSignaturePad';

interface MobileSignatureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName: string;
  signatureData: string | null;
  onSignatureChange: (data: string | null) => void;
  existingSignature?: string | null;
  disabled?: boolean;
  onConfirm: () => void;
  isProcessing?: boolean;
}

const MobileSignatureModal: React.FC<MobileSignatureModalProps> = ({
  open,
  onOpenChange,
  clientName,
  signatureData,
  onSignatureChange,
  existingSignature = null,
  disabled = false,
  onConfirm,
  isProcessing = false,
}) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fixed inset-0 w-screen h-screen max-w-none max-h-none m-0 p-4 rounded-none translate-x-0 translate-y-0 top-0 left-0 z-50 flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="h-5 w-5" />
            {t('clients.termsAcceptance.signTermsAndConditions')} - {clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 space-y-4">
          <Alert>
            <AlertDescription>
              {t('clients.termsAcceptance.pleaseSignBelow')}
            </AlertDescription>
          </Alert>

          <div className="flex-1 min-h-0">
            <TermsSignaturePad
              onSignatureChange={onSignatureChange}
              disabled={disabled}
              existingSignature={existingSignature}
              fullScreen={true}
              mobileInModal={true}
              hideLabel={false}
            />
          </div>

          {!signatureData && !existingSignature && (
            <Alert>
              <AlertDescription className="text-sm">
                {t('clients.termsAcceptance.signatureRequired')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={disabled || isProcessing}
          >
            {t('clients.termsAcceptance.cancel')}
          </Button>
          
          <Button
            onClick={handleConfirm}
            disabled={(!signatureData && !existingSignature) || disabled || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('clients.termsAcceptance.processing')}
              </>
            ) : (
              t('clients.termsAcceptance.confirmSignature')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MobileSignatureModal;
